/*
 * $Id:: ResetPasswordDialog.js 2021/02/26 06:09 otsolka
 *
 *        . * .
 *      * RRRR  *   Copyright (c) 2012-2021 EUIPO: European Intelectual
 *     .  RR  R  .  Property Organization (trademarks and designs).
 *     *  RRR    *
 *      . RR RR .   ALL RIGHTS RESERVED
 *       *. _ .*
 *
 *  The use and distribution of this software is under the restrictions exposed in 'license.txt'
 */

import React, { useState, useEffect } from 'react';

import Button from '@material-ui/core/Button';
import CircularProgress from '@material-ui/core/CircularProgress';
import Dialog from '@material-ui/core/Dialog';
import DialogActions from '@material-ui/core/DialogActions';
import DialogContent from '@material-ui/core/DialogContent';
import DialogTitle from '@material-ui/core/DialogTitle';
import Grid from '@material-ui/core/Grid';
import IconButton from '@material-ui/core/IconButton';
import Input from '@material-ui/core/Input';
import InputLabel from '@material-ui/core/InputLabel';
import { makeStyles } from '@material-ui/core/styles';
import Typography from '@material-ui/core/Typography';
import VisibilityOffOutlined from '@material-ui/icons/VisibilityOffOutlined';
import VisibilityOutlined from '@material-ui/icons/VisibilityOutlined';
import { useSelector, useDispatch } from 'react-redux';

import inputsMaxChars from '../../../../app/inputsMaxChars';
import ErrorInput from '../../../../components/ErrorInput';
import { t } from '../../../../providers';
import { error as errorSlice, loading as loadingSlice, requestResult } from '../../accountSlice';
import { SLICE_NAME } from '../../constants';

const useStyles = makeStyles((theme) => ({
    dialogOverride: {
        color: 'black',
        '& .MuiDialog-paper': {
            padding: '6px',
            paddingTop: '14px',
            maxWidth: '500px',
        },
    },
    email: {
        color: theme.palette.text.mainBlue,
    },
    inputWrapper: {
        marginBottom: '1.5rem',
    },
    textInput: {
        height: '2.5rem',
        width: '14rem',
        marginTop: '10px',
        marginBottom: '10px',
        '@media (max-width:600px)': {
            width: '12.5rem',
        },
    },
    inputItem: {
        marginBottom: '10px',
        flex: '1 1 auto',
    },
    inputError: {
        marginTop: '5px',
    },
    fieldLabel: {
        color: theme.palette.text.label,
        fontSize: '0.625rem',
        fontWeight: 'bold',
        textTransform: 'uppercase',
        letterSpacing: 'normal',
    },
    actionButton: {
        minWidth: '66px',
    },
}));

const ResetPasswordDialog = ({ open, closeDialog }) => {
    const classes = useStyles();
    const error = useSelector(errorSlice);
    const loading = useSelector(loadingSlice);
    const response = useSelector(requestResult);
    const dispatch = useDispatch();

    const [password, setPassword] = useState({ value: '', isDirty: false, showPassword: false, isBlured: false });
    const [oldPassword, setOldPassword] = useState({ value: '', isDirty: false, showPassword: false, isBlured: false });
    const [confirmPassword, setConfirmPassword] = useState({ value: '', isDirty: false, showPassword: false, isBlured: false });
    const [passwordsMatch, setPasswordsMatch] = useState(false);

    const handleCloseDialog = () => {
        /* reset input fields */
        setOldPassword({ value: '', isDirty: false, showPassword: false, isBlured: false });
        setPassword({ value: '', isDirty: false, showPassword: false, isBlured: false });
        setConfirmPassword({ value: '', isDirty: false, showPassword: false, isBlured: false });
        /* clear account/error if there is error */
        if (!!error) dispatch({ type: `${SLICE_NAME}/error`, payload: false });

        closeDialog();
    };

    useEffect(() => {
        if (response && response.ok) {
            dispatch({ type: `${SLICE_NAME}/requestResult`, payload: null });
            handleCloseDialog();
        }
    }, [dispatch, response]);

    const handleOldPasswordChange = (event) => {
        const value = event.target.value;
        if (!inputsMaxChars.password || value.length <= inputsMaxChars.password) {
            setOldPassword({ ...oldPassword, value, isDirty: true });
        }
    };

    const handlePasswordChange = (event) => {
        const value = event.target.value;
        if (!inputsMaxChars.password || value.length <= inputsMaxChars.password) {
            setPassword({ ...password, value, isDirty: true });
            setPasswordsMatch(event.target.value === confirmPassword.value);
        }
    };

    const handleConfirmPasswordChange = (event) => {
        if (!inputsMaxChars.password || event.target.value.length <= inputsMaxChars.password) {
            setConfirmPassword({ ...password, value: event.target.value, isDirty: true });
            setPasswordsMatch(event.target.value === password.value);
        }
    };

    const handleOnBlurPassword = (passwd, setPasswd) => {
        if (!passwd.isBlured) setPasswd({ ...passwd, isBlured: true });
    };

    const submitPassword = (event) => {
        event.preventDefault();
        if (!password.value || !passwordsMatch) {
            setOldPassword({ ...oldPassword, isDirty: true, isBlured: true });
            setPassword({ ...password, isDirty: true, isBlured: true });
            setConfirmPassword({ ...confirmPassword, isDirty: true, isBlured: true });
            return;
        }
        const payload = { oldPassword: oldPassword.value, newPassword: password.value };
        dispatch({ type: `${SLICE_NAME}/updatePassword`, payload });
    };

    const toggleVisibility = (passwd, setPasswd) => {
        setPasswd({ ...passwd, showPassword: !passwd.showPassword });
    };

    const passwordsDoNotMatch = !!password.value && password.isBlured && confirmPassword.isBlured && password.value !== confirmPassword.value;

    return (
        <Dialog open={open} onClose={handleCloseDialog} aria-labelledby="reset-account-accept-dialog" className={classes.dialogOverride}>
            <form onSubmit={submitPassword} noValidate>
                <DialogTitle id="form-dialog-title">{t('update.password.dialog')}</DialogTitle>
                <DialogContent>
                    <Grid container direction="column" className={classes.inputWrapper}>
                        <Grid item xs sm md className={classes.inputItem}>
                            <InputLabel
                                role="input"
                                htmlFor="oldPassword"
                                error={(oldPassword.isDirty && !oldPassword.value) || passwordsDoNotMatch}
                                className={classes.fieldLabel}>
                                {t('old.password')}*
                            </InputLabel>
                            <Input
                                id="oldPassword"
                                role="input"
                                className={classes.textInput}
                                placeholder={t('type.old.password')}
                                type={oldPassword.showPassword ? 'text' : 'password'}
                                name="oldPassword"
                                fullWidth
                                autoFocus
                                onChange={handleOldPasswordChange}
                                onBlur={() => handleOnBlurPassword(oldPassword, setOldPassword)}
                                value={oldPassword.value}
                                endAdornment={
                                    <IconButton onClick={() => toggleVisibility(oldPassword, setOldPassword)} type="button" disableRipple>
                                        {oldPassword.showPassword ? (
                                            <VisibilityOffOutlined aria-hidden="true" focusable="false" />
                                        ) : (
                                            <VisibilityOutlined aria-hidden="true" focusable="false" />
                                        )}
                                    </IconButton>
                                }
                                error={oldPassword.isDirty && !oldPassword.value}
                            />
                            {oldPassword.isDirty && !oldPassword.value && (
                                <ErrorInput errorMessage={t('required.field')} classes={`${classes.inputError}`} />
                            )}
                        </Grid>
                        <Grid item xs sm md className={classes.inputItem}>
                            <InputLabel
                                role="input"
                                htmlFor="password"
                                error={(password.isDirty && !password.value) || passwordsDoNotMatch}
                                className={classes.fieldLabel}>
                                {t('new.password')}*
                            </InputLabel>
                            <Input
                                role="input"
                                id="password"
                                className={classes.textInput}
                                placeholder={t('type.password')}
                                type={password.showPassword ? 'text' : 'password'}
                                name="password"
                                fullWidth
                                onChange={handlePasswordChange}
                                onBlur={() => handleOnBlurPassword(password, setPassword)}
                                value={password.value}
                                endAdornment={
                                    <IconButton onClick={() => toggleVisibility(password, setPassword)} type="button" disableRipple>
                                        {password.showPassword ? (
                                            <VisibilityOffOutlined aria-hidden="true" focusable="false" />
                                        ) : (
                                            <VisibilityOutlined aria-hidden="true" focusable="false" />
                                        )}
                                    </IconButton>
                                }
                                error={(password.isDirty && !password.value) || passwordsDoNotMatch}
                            />
                            {password.isDirty && !password.value && (
                                <ErrorInput errorMessage={t('required.field')} classes={`${classes.inputError}`} />
                            )}
                            {passwordsDoNotMatch && <ErrorInput errorMessage={t('entering.same.value')} classes={`${classes.inputError}`} />}
                        </Grid>
                        <Grid item xs sm md className={classes.inputItem}>
                            <InputLabel
                                role="input"
                                htmlFor="confirmPassword"
                                error={(confirmPassword.isDirty && !confirmPassword.value) || passwordsDoNotMatch}
                                className={classes.fieldLabel}>
                                {t('repeat.new.password')}*
                            </InputLabel>
                            <Input
                                role="input"
                                id="confirmPassword"
                                role="input"
                                className={classes.textInput}
                                placeholder={t('type.password')}
                                type={confirmPassword.showPassword ? 'text' : 'password'}
                                name="confirmPassword"
                                fullWidth
                                onChange={handleConfirmPasswordChange}
                                onBlur={() => handleOnBlurPassword(confirmPassword, setConfirmPassword)}
                                value={confirmPassword.value}
                                endAdornment={
                                    <IconButton onClick={() => toggleVisibility(confirmPassword, setConfirmPassword)} type="button" disableRipple>
                                        {confirmPassword.showPassword ? (
                                            <VisibilityOffOutlined aria-hidden="true" focusable="false" />
                                        ) : (
                                            <VisibilityOutlined aria-hidden="true" focusable="false" />
                                        )}
                                    </IconButton>
                                }
                                error={(confirmPassword.isDirty && !confirmPassword.value) || passwordsDoNotMatch}
                            />
                            {confirmPassword.isDirty && !confirmPassword.value && (
                                <ErrorInput errorMessage={t('required.field')} classes={`${classes.inputError}`} />
                            )}
                        </Grid>
                        {!!error && (
                            <Grid item xs sm md>
                                <Typography aria-label={t(error)} color="error" className={classes.rPInfoMessage}>
                                    {t(error)}
                                </Typography>
                            </Grid>
                        )}
                    </Grid>
                </DialogContent>
                <DialogActions>
                    {!loading ? (
                        <>
                            <Button className={classes.actionButton} variant="contained" onClick={handleCloseDialog} type="button">
                                {t('back')}
                            </Button>
                            <Button className={classes.actionButton} variant="outlined" onClick={submitPassword}>
                                {t('save')}
                            </Button>
                        </>
                    ) : (
                        <CircularProgress />
                    )}
                </DialogActions>
            </form>
        </Dialog>
    );
};

export default ResetPasswordDialog;
